import { trpc } from "@/lib/trpc";
import { useAuth, hasUserAuth } from "@/lib/auth";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { AlertCircle, AlertTriangle, HardDrive } from "lucide-react";
import { cn } from "@/lib/utils";

export type LimitableResource =
  | "walkthroughs"
  | "sources"
  | "projects"
  | "storage"
  | "any";

interface LimitReachedBannerProps {
  /** Which resource to check: walkthroughs, sources, projects, storage, or any */
  resource?: LimitableResource;
  /** Whether to show warnings when approaching limits (>= 80%). Defaults to true. */
  showApproaching?: boolean;
  className?: string;
}

function formatBytes(bytes: number): string {
  const mb = bytes / (1024 * 1024);
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
  return `${Math.round(mb)} MB`;
}

interface EvaluatedLimit {
  resource: "walkthroughs" | "sources" | "projects" | "storage";
  isExceeded: boolean;
  isApproaching: boolean;
  used: number;
  limit: number;
  percent: number;
  title: string;
  description: string;
  actionText?: string;
  actionHref?: string;
}

/**
 * Shows an inline or header banner when the workspace has exceeded or is
 * approaching (>= 80%) its plan limits for launch-enforced resources (storage, runs, projects).
 */
export function LimitReachedBanner({
  resource = "any",
  showApproaching = true,
  className,
}: LimitReachedBannerProps) {
  const { auth } = useAuth();
  const { data: planSummary } = trpc.usage.planSummary.useQuery(undefined, {
    enabled: hasUserAuth(auth),
  });

  if (!hasUserAuth(auth) || !planSummary) return null;

  const evaluated: EvaluatedLimit[] = [];

  // 1. Storage check
  if (
    planSummary.storage &&
    !planSummary.storage.unlimited &&
    planSummary.storage.limit > 0
  ) {
    const { used, limit } = planSummary.storage;
    const percent = Math.round((used / limit) * 100);
    const isExceeded = used >= limit;
    const isApproaching = percent >= 80 && !isExceeded;

    if (isExceeded || (isApproaching && showApproaching)) {
      evaluated.push({
        resource: "storage",
        isExceeded,
        isApproaching,
        used,
        limit,
        percent,
        title: isExceeded
          ? "Artifact Storage Limit Reached"
          : `Approaching Storage Limit (${percent}%)`,
        description: isExceeded
          ? `Your workspace has reached its ${formatBytes(limit)} artifact storage limit. New walkthrough pushes will be rejected. Delete older runs in your projects to free up space.`
          : `Your workspace is using ${formatBytes(used)} of ${formatBytes(limit)}. Delete older walkthrough runs in your projects to avoid interrupted test runs.`,
        actionText: "Manage Storage",
        actionHref: "/settings",
      });
    }
  }

  // 2. Walkthroughs check
  if (
    planSummary.walkthroughs &&
    !planSummary.walkthroughs.unlimited &&
    planSummary.walkthroughs.limit > 0
  ) {
    const { used, limit } = planSummary.walkthroughs;
    const percent = Math.round((used / limit) * 100);
    const isExceeded = used >= limit;
    const isApproaching = percent >= 80 && !isExceeded;

    if (isExceeded || (isApproaching && showApproaching)) {
      evaluated.push({
        resource: "walkthroughs",
        isExceeded,
        isApproaching,
        used,
        limit,
        percent,
        title: isExceeded
          ? "Monthly Walkthrough Limit Reached"
          : `Approaching Monthly Walkthrough Limit (${used}/${limit})`,
        description: isExceeded
          ? `You've completed all ${limit} monthly walkthrough runs on your plan. Reach out during Public Beta for a fair-use limit adjustment.`
          : `You've used ${used} of ${limit} monthly walkthrough runs. Reach out to support if you need more capacity during Public Beta.`,
        actionText: "Contact Support",
        actionHref:
          "mailto:hello@iamu.dev?subject=Walkthrough%20Limit%20Adjustment",
      });
    }
  }

  // 3. Projects check
  if (
    planSummary.projects &&
    !planSummary.projects.unlimited &&
    planSummary.projects.limit > 0
  ) {
    const { used, limit } = planSummary.projects;
    const percent = Math.round((used / limit) * 100);
    const isExceeded = used >= limit;
    const isApproaching = percent >= 80 && !isExceeded;

    if (isExceeded || (isApproaching && showApproaching)) {
      evaluated.push({
        resource: "projects",
        isExceeded,
        isApproaching,
        used,
        limit,
        percent,
        title: isExceeded
          ? "Project Limit Reached"
          : `Approaching Project Limit (${used}/${limit})`,
        description: isExceeded
          ? `You have reached the maximum allowed ${limit} projects for your workspace. Remove unused projects to add new ones.`
          : `You are currently using ${used} of ${limit} allowed workspace projects.`,
        actionText: "View Projects",
        actionHref: "/projects",
      });
    }
  }

  // 4. Sources check
  if (
    planSummary.sources &&
    !planSummary.sources.unlimited &&
    planSummary.sources.limit > 0
  ) {
    const { used, limit } = planSummary.sources;
    const percent = Math.round((used / limit) * 100);
    const isExceeded = used >= limit;
    const isApproaching = percent >= 80 && !isExceeded;

    if (isExceeded || (isApproaching && showApproaching)) {
      evaluated.push({
        resource: "sources",
        isExceeded,
        isApproaching,
        used,
        limit,
        percent,
        title: isExceeded
          ? "Knowledge Sources Limit Reached"
          : `Approaching Knowledge Sources Limit (${used}/${limit})`,
        description: isExceeded
          ? `You've reached your limit of ${limit} domain sources. Remove unneeded sources to add more.`
          : `You've used ${used} of ${limit} domain sources.`,
      });
    }
  }

  // Filter by requested resource
  const matching =
    resource === "any"
      ? evaluated
      : evaluated.filter((e) => e.resource === resource);

  if (matching.length === 0) return null;

  // Prioritize exceeded first, then highest percentage
  const target = matching.sort((a, b) => {
    if (a.isExceeded && !b.isExceeded) return -1;
    if (!a.isExceeded && b.isExceeded) return 1;
    return b.percent - a.percent;
  })[0];

  if (!target) return null;

  const isDestructive = target.isExceeded;

  return (
    <Alert
      variant={isDestructive ? "destructive" : "default"}
      className={cn(
        "flex flex-col sm:flex-row sm:items-center justify-between gap-3",
        !isDestructive &&
          "border-amber-500/40 bg-amber-500/5 text-amber-800 dark:text-amber-300",
        className,
      )}
    >
      <div className="flex items-start gap-3">
        {target.resource === "storage" ? (
          <HardDrive
            className={cn(
              "h-5 w-5 shrink-0 mt-0.5",
              isDestructive ? "text-destructive" : "text-amber-500",
            )}
          />
        ) : isDestructive ? (
          <AlertCircle className="h-5 w-5 shrink-0 text-destructive mt-0.5" />
        ) : (
          <AlertTriangle className="h-5 w-5 shrink-0 text-amber-500 mt-0.5" />
        )}
        <div className="space-y-0.5">
          <AlertTitle className="font-semibold text-sm">
            {target.title}
          </AlertTitle>
          <AlertDescription className="text-xs text-muted-foreground">
            {target.description}
          </AlertDescription>
        </div>
      </div>

      {target.actionText && target.actionHref && (
        <div className="shrink-0 self-end sm:self-center">
          {target.actionHref.startsWith("mailto:") ? (
            <Button size="sm" variant="outline" asChild>
              <a href={target.actionHref}>{target.actionText}</a>
            </Button>
          ) : (
            <Button size="sm" variant="outline" asChild>
              <Link to={target.actionHref}>{target.actionText}</Link>
            </Button>
          )}
        </div>
      )}
    </Alert>
  );
}
